/**
 * Task Polling Hook
 *
 * Polls task status during async generation (document upload, prompt, from-rule).
 * Uses lazy query with manual setInterval for full control over polling.
 * Automatically stops polling when task reaches terminal state.
 * Includes timeout protection (5 minutes max).
 */

import { useCallback, useEffect, useRef, useState } from 'react';

import {
  isFailed,
  isTerminalStatus,
  type TaskStatusResponse,
  useLazyGetTaskStatusQuery,
} from '../store/api';

// Polling interval in milliseconds
const POLL_INTERVAL_MS = 2000;

// Maximum polling duration (5 minutes)
const MAX_POLLING_DURATION_MS = 5 * 60 * 1000;

export interface UseTaskPollingOptions {
  /** Callback when task reaches ready/completed state */
  onComplete?: (task: TaskStatusResponse) => void;
  /** Callback when task fails (status: "failed") */
  onFailed?: (task: TaskStatusResponse) => void;
  /** Callback when polling times out */
  onTimeout?: () => void;
  /** Callback on error */
  onError?: (error: unknown) => void;
  /** Whether to start polling immediately (default: true) */
  enabled?: boolean;
}

export interface UseTaskPollingResult {
  /** Current task data */
  task: TaskStatusResponse | undefined;
  /** Current task status */
  status: string | undefined;
  /** True on initial load only */
  isLoading: boolean;
  /** True during any fetch (initial or polling) */
  isFetching: boolean;
  /** True when actively polling */
  isPolling: boolean;
  /** True if polling timed out */
  hasTimedOut: boolean;
  /** True if task generation failed */
  hasFailed: boolean;
  /** Error if any */
  error: unknown;
  /** Manually trigger refetch */
  refetch: () => void;
  /** Stop polling manually */
  stopPolling: () => void;
  /** Start polling manually */
  startPolling: () => void;
}

/**
 * Hook for polling task status during async generation
 *
 * @param taskId - The rule build task ID (null to skip)
 * @param options - Polling options
 * @returns Polling state and controls
 */
export const useTaskPolling = (
  taskId: string | null,
  options: UseTaskPollingOptions = {},
): UseTaskPollingResult => {
  const { onComplete, onFailed, onTimeout, onError, enabled = true } = options;

  // State
  const [task, setTask] = useState<TaskStatusResponse | undefined>(undefined);
  const [isLoading, setIsLoading] = useState(false);
  const [isFetching, setIsFetching] = useState(false);
  const [isPolling, setIsPolling] = useState(false);
  const [hasTimedOut, setHasTimedOut] = useState(false);
  const [hasFailed, setHasFailed] = useState(false);
  const [error, setError] = useState<unknown>(null);

  // Refs for polling control
  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const pollingStartTimeRef = useRef<number | null>(null);
  const currentTaskIdRef = useRef<string | null>(null);

  // Store callbacks in refs to avoid stale closures
  const onCompleteRef = useRef(onComplete);
  const onFailedRef = useRef(onFailed);
  const onTimeoutRef = useRef(onTimeout);
  const onErrorRef = useRef(onError);

  useEffect(() => {
    onCompleteRef.current = onComplete;
    onFailedRef.current = onFailed;
    onTimeoutRef.current = onTimeout;
    onErrorRef.current = onError;
  }, [onComplete, onFailed, onTimeout, onError]);

  // Lazy query for manual control
  const [triggerPoll] = useLazyGetTaskStatusQuery();

  // Stop polling helper
  const stopPolling = useCallback(() => {
    console.log('[useTaskPolling] stopPolling called');
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
    pollingStartTimeRef.current = null;
    setIsPolling(false);
  }, []);

  // Poll function
  const poll = useCallback(async () => {
    const pollTaskId = currentTaskIdRef.current;
    if (!pollTaskId) {
      console.log('[useTaskPolling] poll: no taskId, skipping');
      return;
    }

    // Check timeout
    if (pollingStartTimeRef.current) {
      const elapsed = Date.now() - pollingStartTimeRef.current;
      if (elapsed >= MAX_POLLING_DURATION_MS) {
        console.log('[useTaskPolling] poll: timeout reached');
        stopPolling();
        setHasTimedOut(true);
        onTimeoutRef.current?.();
        return;
      }
    }

    console.log('[useTaskPolling] poll: fetching status for', pollTaskId);
    setIsFetching(true);

    try {
      const result = await triggerPoll({ taskId: pollTaskId }).unwrap();
      // Backend returns full TaskResponse, cast through unknown
      const typedResult = result as unknown as TaskStatusResponse;
      
      console.log('[useTaskPolling] poll: got status', typedResult.status);
      setTask(typedResult);
      setError(null);

      // Check if terminal status
      if (isTerminalStatus(typedResult.status)) {
        console.log('[useTaskPolling] poll: terminal status, stopping');
        stopPolling();

        if (isFailed(typedResult.status)) {
          setHasFailed(true);
          onFailedRef.current?.(typedResult);
        } else {
          onCompleteRef.current?.(typedResult);
        }
      }
    } catch (err) {
      console.error('[useTaskPolling] poll: error', err);
      setError(err);
      stopPolling();
      onErrorRef.current?.(err);
    } finally {
      setIsFetching(false);
      setIsLoading(false);
    }
  }, [triggerPoll, stopPolling]);

  // Start polling helper
  const startPolling = useCallback(() => {
    const startTaskId = currentTaskIdRef.current;
    if (!startTaskId || hasTimedOut) {
      console.log('[useTaskPolling] startPolling: cannot start', { startTaskId, hasTimedOut });
      return;
    }

    console.log('[useTaskPolling] startPolling: starting for', startTaskId);

    // Clear any existing interval
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
    }

    pollingStartTimeRef.current = Date.now();
    setIsPolling(true);
    setIsLoading(true);

    // Initial poll
    void poll();

    // Set up polling interval
    pollingIntervalRef.current = setInterval(() => {
      void poll();
    }, POLL_INTERVAL_MS);
  }, [hasTimedOut, poll]);

  // Reset and start polling when taskId changes
  useEffect(() => {
    console.log('[useTaskPolling] taskId changed:', taskId, 'enabled:', enabled);

    // Cleanup previous polling
    stopPolling();

    // Reset state
    setTask(undefined);
    setHasTimedOut(false);
    setHasFailed(false);
    setError(null);
    setIsLoading(false);
    setIsFetching(false);

    if (taskId && enabled) {
      currentTaskIdRef.current = taskId;
      startPolling();
    } else {
      currentTaskIdRef.current = null;
    }
  }, [taskId, enabled, stopPolling, startPolling]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
    };
  }, []);

  // Manual refetch
  const refetch = useCallback(() => {
    void poll();
  }, [poll]);

  return {
    task,
    status: task?.status,
    isLoading,
    isFetching,
    isPolling,
    hasTimedOut,
    hasFailed,
    error,
    refetch,
    stopPolling,
    startPolling,
  };
};

export default useTaskPolling;
